/**
 * Demo Website Traffic Page
 * 
 * A fully self-contained demo page that looks identical to the real Website Traffic page
 * but uses hardcoded demo data instead of making API calls.
 * 
 * This page is ONLY shown to the demo tenant (Quantum Leap Labs).
 */
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { 
  Globe, Users, Eye, Clock, TrendingUp, TrendingDown, 
  BarChart3, PieChart, MapPin, Calendar, ArrowUpRight
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import DateFilter from "@/components/DateFilterDropdown";

// Demo data - hardcoded for Quantum Leap Labs
const generateDemoData = (days = 30) => {
  const data = [];
  const now = new Date();
  
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    const baseSessions = isWeekend ? 320 : 580;
    const variance = Math.random() * 0.3 - 0.15;
    const sessions = Math.round(baseSessions * (1 + variance));
    
    data.push({
      date: date.toISOString().split('T')[0],
      sessions,
      users: Math.round(sessions * 0.72),
      pageviews: Math.round(sessions * 2.8),
      bounceRate: Math.round((35 + Math.random() * 20) * 10) / 10,
      avgDuration: Math.round(120 + Math.random() * 180)
    });
  }
  
  return data;
};

const TRAFFIC_SOURCES = [
  { name: "Organic Search", sessions: 4250, color: "#22c55e" },
  { name: "Direct", sessions: 2890, color: "#3b82f6" },
  { name: "LinkedIn", sessions: 1820, color: "#0077b5" },
  { name: "Referral", sessions: 1240, color: "#f59e0b" },
  { name: "Email", sessions: 980, color: "#ef4444" },
  { name: "Paid Search", sessions: 720, color: "#8b5cf6" },
  { name: "AI Referral", sessions: 340, color: "#06b6d4" }
];

const REGIONS = [
  { name: "North America", sessions: 6420, percentage: 52 },
  { name: "EMEA", sessions: 3580, percentage: 29 },
  { name: "APAC", sessions: 1720, percentage: 14 },
  { name: "LATAM", sessions: 520, percentage: 5 }
];

const TOP_COUNTRIES = [
  { country: "United States", sessions: 5240, flag: "🇺🇸" },
  { country: "United Kingdom", sessions: 1420, flag: "🇬🇧" },
  { country: "Canada", sessions: 980, flag: "🇨🇦" },
  { country: "Germany", sessions: 720, flag: "🇩🇪" },
  { country: "Australia", sessions: 580, flag: "🇦🇺" },
  { country: "France", sessions: 420, flag: "🇫🇷" },
  { country: "Netherlands", sessions: 340, flag: "🇳🇱" },
  { country: "Japan", sessions: 280, flag: "🇯🇵" }
];

const TOP_PAGES = [
  { page: "/", pageviews: 8420, title: "Home" },
  { page: "/pricing", pageviews: 4280, title: "Pricing" },
  { page: "/features", pageviews: 3640, title: "Features" },
  { page: "/demo", pageviews: 2890, title: "Request Demo" },
  { page: "/blog", pageviews: 2120, title: "Blog" },
  { page: "/about", pageviews: 1580, title: "About Us" }
];

export default function DemoWebsiteTrafficPage() {
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  
  const dailyData = useMemo(() => generateDemoData(30), []);
  
  const totals = useMemo(() => {
    return {
      sessions: dailyData.reduce((sum, d) => sum + d.sessions, 0),
      users: dailyData.reduce((sum, d) => sum + d.users, 0),
      pageviews: dailyData.reduce((sum, d) => sum + d.pageviews, 0),
      avgBounceRate: (dailyData.reduce((sum, d) => sum + d.bounceRate, 0) / dailyData.length).toFixed(1),
      avgDuration: Math.round(dailyData.reduce((sum, d) => sum + d.avgDuration, 0) / dailyData.length)
    };
  }, [dailyData]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="demo-website-traffic-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">Website Traffic</h1>
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            DEMO DATA
          </Badge>
        </div>
        <DateFilter 
          pageId="website-traffic"
          onDateChange={(start, end) => setDateRange({ start, end })}
        />
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Sessions</p>
                <p className="text-2xl font-bold text-white">{totals.sessions.toLocaleString()}</p>
              </div>
              <Globe className="w-8 h-8 text-blue-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+12.4%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Users</p>
                <p className="text-2xl font-bold text-white">{totals.users.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-green-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+8.7%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Page Views</p>
                <p className="text-2xl font-bold text-white">{totals.pageviews.toLocaleString()}</p>
              </div>
              <Eye className="w-8 h-8 text-purple-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+15.2%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Bounce Rate</p>
                <p className="text-2xl font-bold text-white">{totals.avgBounceRate}%</p>
              </div>
              <ArrowUpRight className="w-8 h-8 text-orange-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingDown className="w-3 h-3" />
              <span>-3.1%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg. Duration</p>
                <p className="text-2xl font-bold text-white">{formatDuration(totals.avgDuration)}</p>
              </div>
              <Clock className="w-8 h-8 text-cyan-400" />
            </div>
            <div className="flex items-center gap-1 mt-2 text-green-400 text-sm">
              <TrendingUp className="w-3 h-3" />
              <span>+5.8%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sessions Over Time */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Sessions Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="sessionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748b"
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#64748b" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  labelStyle={{ color: '#f1f5f9' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="sessions" 
                  stroke="#3b82f6" 
                  fill="url(#sessionGradient)" 
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Traffic Sources */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-green-400" />
              Traffic Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={TRAFFIC_SOURCES}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="sessions"
                  nameKey="name"
                >
                  {TRAFFIC_SOURCES.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value) => [value.toLocaleString() + ' sessions', '']}
                />
                <Legend 
                  verticalAlign="middle" 
                  align="right" 
                  layout="vertical"
                  formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Regions */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-400" />
              By Region
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {REGIONS.map((region) => (
                <div key={region.name} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{region.name}</span>
                    <span className="text-slate-400">{region.sessions.toLocaleString()} ({region.percentage}%)</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${region.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Countries */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Top Countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {TOP_COUNTRIES.slice(0, 6).map((country, idx) => (
                <div key={country.country} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-slate-300 text-sm">{country.country}</span>
                  </div>
                  <span className="text-slate-400 text-sm">{country.sessions.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-orange-400" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {TOP_PAGES.map((page, idx) => (
                <div key={page.page} className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-300 text-sm">{page.title}</p>
                    <p className="text-slate-500 text-xs">{page.page}</p>
                  </div>
                  <span className="text-slate-400 text-sm">{page.pageviews.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
